<script lang="ts">
    import { createEventDispatcher, onMount, tick } from 'svelte'
    import Fa from 'svelte-fa'
    import { faAdd, faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import { Appointment } from '../../modules/schedulerApi/appointments/appointment'
    import type { CreateAppointmentDto } from '../../modules/schedulerApi/appointments/dto/create-appointment.dto'
    import type { UpdateAppointmentDto } from '../../modules/schedulerApi/appointments/dto/upate-appointment.dto'
    import { AppointmentStatus } from '../../modules/schedulerApi/appointments/appointments.enum'
    import dayjs from 'dayjs'
    import { AppointmentRepeat } from '../../modules/schedulerApi/appointments/appointment-repeat'
    import { User } from '../../modules/usersApi/users/user'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import type { FormDataLastEmissionDto } from '../../modules/toolsApi/forms/form-datas/dto/form-data-last-emission.dto'
    import Popover from '../_common/popover.svelte'
    import AppointmentFormRequiredDocuments from './appointment-form-required-documents.svelte'
    import type { Schedule } from '../../modules/schedulerApi/schedules/schedule'
    import ZentitySelect from '../_common/z/zentity-select.svelte'
    import type { Person } from '../../modules/eventsApi/persons/person'
    import AppointmentFormBlock from './appointment-form-block.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import { slide } from 'svelte/transition'
    import { intervaloInicial } from './appointment-dates'

    // ##############################################################################################################
    const dispatch = createEventDispatcher()

    export let appointmentId: string = null
    export let personId: string = null

    export let dates: {
        start: Date
        end: Date
        scheduleId: string
        auto: boolean
        items?: { serviceId: string; userId?: string; parentServicesOnInvoicesId?: string }[]
    } = null

    let isMobile = window.innerWidth <= 900
    console.log('isMobile', isMobile, window.innerWidth)

    let entities: Appointment[] = []
    let snapshots: { isDirty: boolean; snapshot: string }[] = []

    let person: Person = null
    let comissionSource = null

    let schedules: Schedule[] = []
    async function loadSchedules() {
        schedules = await window.api.schedulers.schedules.getAll()
        schedules = schedules.filter((el) => el.status == 100)
    }

    let noPaid: number = 0
    async function loadInvoices(personId) {
        noPaid = 0
        if (!personId) return
        noPaid = await window.api.pos.invoices.getCreditAndDebitsFromPerson(personId)
    }

    // Mesmo prefixo usado em services.svelte pra diferenciar, no multiselect de "documentos
    // obrigatórios", quais IDs são de templates v2 (o histórico de emissão de cada versão vive em
    // endpoints/tabelas diferentes — form-data (v1) e form-v2-forms (v2)).
    const V2_DOCUMENT_PREFIX = 'v2:'

    let pendingDocuments: FormDataLastEmissionDto[] = []
    async function checkRequiredDocument(entity) {
        pendingDocuments = []
        let formTemplateIds = []
        for (const item of entity.items) {
            if (!item.service.documents) continue
            for (const id of item.service.documents) {
                if (formTemplateIds.some((el) => el === id)) continue
                formTemplateIds.push(id)
            }
        }
        if (formTemplateIds.length > 0) {
            const v1Ids = formTemplateIds.filter((id) => !id.startsWith(V2_DOCUMENT_PREFIX))
            const v2Ids = formTemplateIds.filter((id) => id.startsWith(V2_DOCUMENT_PREFIX)).map((id) => id.slice(V2_DOCUMENT_PREFIX.length))
            const [v1Emissions, v2Emissions] = await Promise.all([
                v1Ids.length > 0 ? window.api.tools.forms.formDatas.findLastEmissionsFromPerson(entity.personId, v1Ids) : [],
                v2Ids.length > 0 ? window.api.tools.formsV2.forms.findLastEmissionsFromPerson(entity.personId, v2Ids) : [],
            ])
            pendingDocuments = [...v1Emissions, ...v2Emissions].filter((el) => el.emited === false || el.status < 100)
            console.log('pending', v1Emissions, v2Emissions)
        }

        return pendingDocuments.length > 0
    }

    function clearEntities() {
        entities = []
        snapshots = []
    }

    async function loadEntity(id: string) {
        entities = []
        snapshots = []
        await appendEntity(id)
    }
    async function appendEntity(id: string) {
        console.log('added entity')
        try {
            show = true
            let _entity = new Appointment()
            _entity = await window.api.schedulers.appointments.getOne(id)
            _entity.start = new Date(_entity.start)
            _entity.end = new Date(_entity.end)

            _entity = await assignObjects(_entity)

            for (let item of _entity.items) {
                item.label = item.service?.name || '[serviço não cadastrado]'
                if (!item.userId) {
                    item.user = new User()
                    item.user.userId = item?.userId
                    item.user.userName = '[usuário não informado]'
                } else {
                    if (!item.user) {
                        item.user = new User()
                        item.user.userId = item?.userId
                        item.user.userName = '[usuário excluído]'
                    }
                }
            }

            if (!person) person = _entity.person
            entities.push(_entity)
            entities = entities
            console.log('appointment', _entity)
            snapshots.push({ snapshot: JSON.stringify(_entity), isDirty: false })
            if (!dates) dates = { start: _entity.start, end: _entity.end, scheduleId: _entity.scheduleId, auto: _entity.auto }
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function assignObjects(entity) {
        entity.items = await window.api.utils.utils.assignUsersFromObject(entity.items)
        entity.items = await window.api.utils.utils.assignServicesFromObject(entity.items, undefined, undefined, true)
        entity.consumption = await window.api.utils.utils.assignServicesFromObject(entity.consumption || [])
        entity = await window.api.utils.utils.assignPersonsFromObject(entity)
        return entity
    }

    async function newEntity() {
        let entity = new Appointment()
        if (!person && personId) {
            entity.personId = person?.personId || personId
            // a função grava entity.person e devolve o próprio entity — a pessoa sai de entity.person,
            // não do retorno (senão `person` vira o agendamento e o nome fica undefined)
            await window.api.utils.utils.assignPersonsFromObject(entity)
            person = entity.person
        }

        if (entities.length === 0) {
            // nunca `new Date(dates.end)` direto: término ausente viraria 1970, não data inválida
            const intervalo = intervaloInicial(dates)
            entity.start = intervalo.start
            entity.end = intervalo.end
        } else {
            entity.start = entities[entities.length - 1].start
            entity.end = entities[entities.length - 1].end
        }
        entity.userId = window.store.token.userId
        entity.scheduleId = dates.scheduleId
        entity.status = AppointmentStatus.agendamento_criado
        entity.items = []
        entity.resources = []
        entity.repeat = new AppointmentRepeat()
        entity.notes = ''
        if (dates) entity.auto = dates.auto

        entities.push(entity)

        // ajusta snap para status do botão salvar
        snapshots.push({ snapshot: JSON.stringify(entity), isDirty: false })

        // adiciona eventuais serviços enviados. tem que ficar apos o snapshot
        if (dates.items?.length > 0) {
            for (const item of dates.items) {
                entity.items.push({
                    userId: item.userId,
                    serviceId: item.serviceId,
                    parentServicesOnInvoicesId: item.parentServicesOnInvoicesId,
                } as any)
            }
            entity.items = await window.api.utils.utils.assignUsersFromObject(entity.items)
            entity.items = await window.api.utils.utils.assignServicesFromObject(entity.items, undefined, undefined, true)
            console.log('intercambio', entity.items)

            // mesmo preenchimento automático de consumíveis (BOM) que roda ao adicionar um serviço manualmente
            // pela tela (appointment-form-block.svelte#addNewItem) — aqui os itens chegam prontos (ex.: vindos
            // da lista de espera), então precisa disparar explicitamente.
            const autoConsumption = await window.api.utils.utils.buildConsumptionFromItems(entity.items)
            if (autoConsumption.length > 0) {
                entity.consumption = [...(entity.consumption || []), ...autoConsumption]
            }
        }

        entities = entities
    }

    async function validateCreateEntity(entity: Appointment) {
        const dto: CreateAppointmentDto = {
            auto: entity.auto,
            end: entity.end,
            items: entity.items.filter((el) => !!el),
            resources: entity.resources.filter((el) => !!el),
            personId: person?.personId,
            scheduleId: entity.scheduleId,
            start: entity.start,
            status: entity.status,
            notes: entity.notes || '',
            summary: person?.name + (entity.notes ? ' / ' + entity.notes : ''),
            repeat: entity.repeat,
            source: 'erp',
            comissionSource,
            userId: entity.userId,
        }
        if (!validateDto(dto)) {
            return false
        }
        if (!(await checkResourcesAvailability(entity))) {
            return false
        }
        if (await checkRequiredDocument(entity)) {
            return false
        }
        return true
    }

    async function createEntity(entity: Appointment, i, fitBlock: boolean) {
        try {
            const dto: CreateAppointmentDto = {
                auto: entity.auto,
                end: entity.end,
                items: entity.items.filter((el) => !!el),
                resources: entity.resources.filter((el) => !!el),
                consumption: (entity.consumption || []).filter((el) => !!el),
                personId: person?.personId,
                scheduleId: entity.scheduleId,
                start: entity.start,
                status: entity.status,
                notes: entity.notes || '',
                summary: person?.name + (entity.notes ? ' / ' + entity.notes : ''),
                repeat: entity.repeat,
                source: 'erp',
                comissionSource,
                userId: entity.userId,
            }
            const result = await window.api.schedulers.appointments.create(dto as any, { fitBlock })
            entities[i] = await assignObjects(result)
            window.api.users.statistics.tick('scheduler.create')
        } catch (error) {
            if (error.statusCode == 409) {
                if (!confirm('Já existe uma pessoa agendada neste horário. Deseja encaixar?')) return
                return await createEntity(entity, i, false)
            }
            console.error(error)
            evaluateError(error)
        } finally {
        }
    }

    async function validateUpdateEntity(entity: Appointment) {
        const dto: UpdateAppointmentDto = {
            auto: entity.auto,
            end: entity.end,
            items: entity.items.filter((el) => !!el),
            resources: entity.resources.filter((el) => !!el),
            personId: person?.personId,
            scheduleId: entity.scheduleId,
            start: entity.start,
            status: entity.status,
            notes: entity.notes || '',
            summary: person?.name + (entity.notes ? ' / ' + entity.notes : ''),
            comissionSource,
        }
        if (!validateDto(dto)) {
            return false
        }
        if (!(await checkResourcesAvailability(entity))) {
            return false
        }
        if (await checkRequiredDocument(entity)) {
            return false
        }
        return true
    }

    async function updateEntity(entity: Appointment, i) {
        try {
            const dto: UpdateAppointmentDto = {
                auto: entity.auto,
                end: entity.end,
                items: entity.items.filter((el) => !!el),
                resources: entity.resources.filter((el) => !!el),
                consumption: (entity.consumption || []).filter((el) => !!el),
                personId: person?.personId,
                scheduleId: entity.scheduleId,
                start: entity.start,
                status: entity.status,
                notes: entity.notes || '',
                summary: person?.name + (entity.notes ? ' / ' + entity.notes : ''),
                comissionSource,
            }
            const result = await window.api.schedulers.appointments.update(entity.appointmentId, dto as any)
            entities[i] = await assignObjects(result)
        } catch (error) {
            evaluateError(error)
        } finally {
        }
    }

    const rangesOverlap = (a: { start: Date; end: Date }, b: { start: Date; end: Date }) =>
        new Date(a.start).getTime() < new Date(b.end).getTime() && new Date(a.end).getTime() > new Date(b.start).getTime()

    // última barreira antes de salvar: a lista de ocupados que o formulário exibe pode estar velha (outra pessoa
    // agendou o mesmo recurso nesse meio tempo), então confere de novo, no horário final do agendamento. também
    // pega o mesmo recurso usado em dois agendamentos deste formulário que se sobrepõem (nenhum está salvo ainda).
    async function checkResourcesAvailability(entity: Appointment) {
        const resources = (entity.resources || []).filter((el) => !!el)
        if (resources.length === 0) return true

        let lockedIds: string[] = []
        try {
            lockedIds = await window.api.schedulers.resourcesLock.lockedIds(entity.start, entity.end, entity.appointmentId)
        } catch (error) {
            evaluateError('Não foi possível conferir se a sala/equipamento está livre neste horário. Tente novamente.')
            return false
        }

        // agendamento já salvo que continua no mesmo horário e com o mesmo recurso: o conflito é anterior a esta
        // checagem e salvar não o piora — não trava a edição de outras coisas (status, observação, serviços)
        const index = entities.indexOf(entity)
        const persisted = entity.appointmentId && snapshots[index] ? JSON.parse(snapshots[index].snapshot) : null
        const keptAsPersisted = (resourcesLockId: string) =>
            !!persisted &&
            new Date(persisted.start).getTime() === new Date(entity.start).getTime() &&
            new Date(persisted.end).getTime() === new Date(entity.end).getTime() &&
            (persisted.resources || []).some((el) => el?.resourcesLockId === resourcesLockId)

        const conflicts = resources.filter((resource) => {
            const inOtherAppointment = lockedIds.includes(resource.resourcesLockId) && !keptAsPersisted(resource.resourcesLockId)
            const inThisForm = entities.some(
                (other) =>
                    other !== entity &&
                    rangesOverlap(other, entity) &&
                    (other.resources || []).some((el) => el?.resourcesLockId === resource.resourcesLockId),
            )
            return inOtherAppointment || inThisForm
        })
        if (conflicts.length === 0) return true

        const labels = conflicts.map((el) => el.resourceLock?.label || 'sala/equipamento').join(', ')
        evaluateError(
            conflicts.length === 1
                ? `Não é possível salvar: ${labels} já está em uso por outro agendamento neste horário.`
                : `Não é possível salvar: ${labels} já estão em uso por outros agendamentos neste horário.`,
        )
        return false
    }

    function validateDto(dto) {
        if (!dto.personId) {
            evaluateError('Selecione o cliente')
            return false
        }

        if (dto.items.length === 0) {
            evaluateError('Informe ao menos um serviço')
            return false
        }
        // horário vazio ou incompleto: sem isto, a comparação abaixo é sempre falsa (qualquer comparação com data
        // inválida é falsa) e o agendamento seguia para o servidor com a data quebrada
        if (!dto.start || !dto.end || isNaN(new Date(dto.start).getTime()) || isNaN(new Date(dto.end).getTime())) {
            evaluateError('Informe o horário de início e de término.')
            return false
        }
        if (dto.start > dto.end) {
            evaluateError('A hora final não pode ser anterior à hora inicial.')
            return false
        }
        if (dayjs(dto.start).diff(dto.end, 'second') === 0) {
            evaluateError('O horário de início e término não podem ser iguais.')
            return false
        }
        for (const item of dto.items) {
            if (item.service?.needResource && dto.resources.length === 0) {
                evaluateError('Ao menos um dos serviços selecionados exige o bloqueio de sala ou equipamento.')
                return false
            }
        }
        for (const item of dto.items) {
            if (!item.userId) {
                evaluateError('Todos os itens devem possuir um atendente.')
                return false
            }
        }

        return true
    }

    // async function onSave(ev, i: number) {
    //     if (ev.detail.appointmentId) {
    //         await updateEntity(entities[i], i)
    //     } else {
    //         await createEntity(entities[i], i)
    //     }
    // }

    async function onSaveAll() {
        let alertDate = false
        for (const entity of entities) {
            if (entity.appointmentId) {
                if (!(await validateUpdateEntity(entity))) return
            } else {
                if (!(await validateCreateEntity(entity))) return
            }
            if (!alertDate) alertDate = entity.start < new Date()
        }
        if (alertDate && !entities[0].appointmentId) {
            if (!confirm('ATENÇÃO: a data/hora selecionada já passou.\nDeseja mesmo adicionar?')) {
                return
            }
        }
        show = true
        for (let index = 0; index < entities.length; index++) {
            if (entities[index].appointmentId) {
                await updateEntity(entities[index], index)
            } else {
                await createEntity(entities[index], index, true)
            }
            snapshots[index] = { snapshot: JSON.stringify(entities[index]), isDirty: false }
        }
        show = false
        dispatchChange()
        dispatchClose()
    }

    $: evaluateDirty(entities)

    $: showLocalActs = entities?.length > 1

    function evaluateDirty(_) {
        for (let index = 0; index < entities.length; index++) {
            snapshots[index].isDirty = JSON.stringify(entities[index]) !== snapshots[index].snapshot
        }
        snapshots = snapshots
    }

    async function removeAll() {
        if (!confirm('Deseja mesmo excluir?')) {
            return
        }
        for (const entity of entities) {
            await onRemove(entity, false)
        }
    }

    async function onRemove(appointment: Appointment, confirmation: boolean = true) {
        console.log(appointment)
        if (appointment?.appointmentId) {
            if (confirmation && !confirm('Deseja mesmo excluir este registro?')) {
                return
            }
            try {
                show = true
                await window.api.schedulers.appointments.remove(appointment?.appointmentId)
                removeEntityByObject(appointment)
                dispatchChange()
                if (entities.length == 0) dispatchClose()
            } catch (error) {
                evaluateError(error)
            } finally {
                show = false
            }
        } else {
            removeEntityByObject(appointment)
        }
    }

    async function onEditAnother(ev) {
        await loadEntity(ev.detail)
    }

    async function onRemoveAnother(ev) {
        console.log(ev.detail)
        if (!confirm('Deseja mesmo excluir este registro?')) {
            return
        }
        try {
            show = true
            await window.api.schedulers.appointments.remove(ev.detail.appointmentId)
            await loadEntity(ev.detail.entity.appointmentId)
            dispatchRefresh()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function removeEntityByObject(ob) {
        const index = entities.indexOf(ob)
        if (index >= 0) {
            entities.splice(index, 1)
            snapshots.splice(index, 1)
        }
        entities = entities
    }

    function getEntityByObject(ob) {
        const index = entities.indexOf(ob)
        if (index >= 0) return entities[index]
        return null
    }

    function evaluateError(error) {
        if (error === "Cannot read properties of undefined (reading 'personId')") {
            error = 'Selecione ou cadastre o cliente'
        }
        window.error(error)
    }

    let show = false

    function dispatchCancel() {
        dispatch('cancel')
    }

    function dispatchChange() {
        dispatch('change')
    }

    function dispatchClose() {
        dispatch(
            'close',
            entities.map((el) => el.appointmentId),
        )
    }

    function dispatchRefresh() {
        dispatch('refresh')
    }

    async function onDispatchReload(ev) {
        await loadEntity(ev.detail)
    }

    let state: 'ready' | 'typing' = 'ready'
    async function onStateChange(ev) {
        state = ev.detail
    }

    onMount(async () => {
        clearEntities()
        console.log('mounting')
        comissionSource = await window.flags('schedulerComission')
        await loadSchedules()
        if (appointmentId) {
            await loadEntity(appointmentId)
        } else {
            await newEntity()
        }
    })

    $: {
        if (person) {
            loadInvoices(person.personId)
        } else {
            noPaid = 0
        }
    }

    function personChanged() {
        for (let entity of entities) {
            entity.personId = person?.personId
        }
    }
</script>

<LoadSpinner {show} />

{#if pendingDocuments.length > 0}
    <Popover title="Documento obrigatório" width="760px" height="50px" on:close={() => (pendingDocuments = [])}>
        <AppointmentFormRequiredDocuments lastEmissions={pendingDocuments}></AppointmentFormRequiredDocuments>
    </Popover>
{/if}

<div class="root m-bottom-sm mobile-m-bottom-sm">
    <div class="grid-act app-form-grid-act">
        <!-- flexbox de propósito (não ZContainer/ZContainerCol): esta barra mistura um campo que
             deve crescer pra preencher o espaço restante (Cliente) com botões de largura fixa em
             pixels (Excluir/Salvar) na MESMA linha — o grid de 12 colunas do ZContainer usa spans
             proporcionais (fração do container), então não há como um span numérico reservar
             "o que sobra depois de dois botões de 120px" de forma confiável em qualquer largura de
             tela. flex-grow resolve isso nativamente, que é o mesmo motivo de .grid-act (acima) já
             ser display:flex. */ -->
        <div class="d-flex flex-grow-1 flex-wrap align-items-end" style="gap:12px;">
            <div class="flex-grow-1" style="min-width:220px;">
                <ZentitySelect fonte="pessoa" label="Cliente" disabled={!!appointmentId} bind:value={person} on:change={personChanged}></ZentitySelect>
            </div>
            {#if isMobile}
                {#if window.store.token.permissions.scheduler.scheduler.write}
                    <div class="w-100">
                        <Zbutton growM={true} class="me-2" grow={true} label="Excluir{entities?.length > 1 ? ' todos' : ''}" color="red" on:click={removeAll}
                        ></Zbutton>
                    </div>
                {/if}
                <div class="w-100">
                    <Zbutton grow={true} disabled={!snapshots.some((el) => el.isDirty) || state == 'typing'} label="Salvar" color="primary" on:click={onSaveAll}
                    ></Zbutton>
                </div>
            {/if}
            {#if window.store.token.permissions.scheduler.scheduler.finances}
                {#if noPaid > 0.1 || noPaid < -0.1}
                    <div class="flex-grow-1" style="min-width:200px;">
                        {#if noPaid > 0.1}
                            <div class="alert alert-warning mb-0 balance-opacity text-center">
                                Saldo em aberto de {noPaid.toFixed(2)}
                            </div>
                        {/if}
                        {#if noPaid < -0.1}
                            <div class="alert alert-success mb-0 balance-opacity text-center">
                                Saldo positivo de {(-noPaid).toFixed(2)}
                            </div>
                        {/if}
                    </div>
                {/if}
            {/if}
            {#if !isMobile}
                {#if window.store.token.permissions.scheduler.scheduler.write}
                    <div style="width:120px;">
                        <Zbutton growM={true} class="me-2" grow={true} label="Excluir{entities?.length > 1 ? ' todos' : ''}" color="red" on:click={removeAll}
                        ></Zbutton>
                    </div>
                {/if}

                <div style="width:120px;">
                    <Zbutton grow={true} disabled={!snapshots.some((el) => el.isDirty) || state == 'typing'} label="Salvar" color="primary" on:click={onSaveAll}
                    ></Zbutton>
                </div>
            {/if}
        </div>
        <!-- {#if window.store.token.permissions.scheduler.scheduler.write}
                {#if !!entities[0].appointmentId}
                    <Zbutton label="Excluir todos" on:click={removeEntity} color="red"></Zbutton>
                {/if}
                <Zbutton label="Salvar todos" on:click={saveEntity} color="primary"></Zbutton>
            {/if} -->
    </div>
    {#if entities.length > 0}
        {#each entities as entity, i}
            <div transition:slide>
                <AppointmentFormBlock
                    bind:entity
                    personId={person?.personId}
                    {schedules}
                    on:remove={(ev) => onRemove(ev.detail)}
                    on:removeAnother={onRemoveAnother}
                    on:editAnother={onEditAnother}
                    on:state={onStateChange}
                    isDirty={snapshots[i].isDirty}
                    showActs={showLocalActs}
                ></AppointmentFormBlock>
            </div>
        {/each}
    {/if}
    <div class="d-flex mt-3 justify-content-center">
        <Zbutton on:click={newEntity} icon={faAdd}></Zbutton>
    </div>
</div>

<style>
    @media (max-width: 900px) {
        .root {
            display: flex;
            flex-direction: column;
        }
        .app-form-grid-act {
            margin: 16px 16px 0px 16px;
            background-color: unset;
            display: flex;
            flex-direction: column;
            gap: 4px;
        }
        :global(.app-form-grid-act > *) {
            width: 100%;
            margin-right: 0px !important;
        }

        .mobile-m-bottom-sm {
            margin-bottom: 256px !important;
        }
    }

    .balance-opacity {
        padding: 9px;
    }
</style>
